import React, { useState } from 'react';
import type { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';

import type { DocNode } from '@atlaskit/adf-schema';
import ButtonGroup from '@atlaskit/button/button-group';
import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Form, {
	Field,
	FormFooter,
	CharacterCounterField,
	ErrorMessage,
	MessageWrapper,
} from '@atlaskit/form';
import Heading from '@atlaskit/heading';
import { CloseButton, ModalBody, useModal } from '@atlaskit/modal-dialog';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import TextArea from '@atlaskit/textarea';
import Tooltip from '@atlaskit/tooltip';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';

import { SnippetEditor } from '../editor/SnippetEditor.tsx';

import { messages } from './SnippetBaseModal.i18n.ts';
import {
	MAX_SNIPPET_NAME_LENGTH,
	MAX_SNIPPET_DESCRIPTION_LENGTH,
	type SnippetBaseModalFormValues,
} from './snippetModalFormUtils.ts';

export type SnippetBaseModalProps = {
	title: React.ReactNode;
	submitLabel: React.ReactNode;
	formKey: string;
	defaultValues: SnippetBaseModalFormValues;
	initialAdf: DocNode | { type: 'doc'; version: 1; content: any[] };
	onSubmit: (values: SnippetBaseModalFormValues, currentAdf: DocNode) => Promise<void>;
	onClose: () => void;
	/** Called when user clicks Cancel (optional; defaults to onClose) */
	onCancel?: () => void;
	onCloseComplete: () => void;
	contentId: string;
	spaceId: string;
	spaceKey: string;
	isSnippetSharingForSiteEnabled: boolean;
} & WithFlagsProps;

const styles = cssMap({
	modalContent: {
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.xlarge'),
	},
	bodyWrapper: {
		display: 'flex',
		flex: 1,
		height: '80vh',
		overflow: 'hidden',
	},
	bodyContainer: {
		display: 'flex',
		flexDirection: 'row',
		gap: token('space.200'),
		flex: 1,
		overflow: 'hidden',
		paddingInline: token('space.050'),
		paddingTop: token('space.150'),
		paddingBottom: token('space.200'),
		boxSizing: 'border-box',
	},
	headerContainer: {
		paddingTop: token('space.150'),
		paddingLeft: token('space.400'),
		paddingRight: token('space.300'),
		flexShrink: 0,
	},
	previewColumn: {
		flex: 1,
		height: '100%',
		borderRadius: token('radius.xlarge'),
		backgroundColor: token('elevation.surface'),
		boxShadow: token('elevation.shadow.raised'),
		transition: 'box-shadow 300ms ease-out',
		overflow: 'auto',
	},
	formColumn: {
		width: '320px',
		minWidth: '320px',
		flexShrink: 0,
		borderRadius: token('radius.xlarge'),
		backgroundColor: token('elevation.surface'),
		boxShadow: token('elevation.shadow.raised'),
		transition: 'box-shadow 300ms ease-out',
		padding: token('space.300'),
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden',
	},
	formContainer: {
		display: 'flex',
		flexDirection: 'column',
		flex: 1,
	},
	formFields: {
		flex: 1,
	},
	formActions: {
		flexShrink: 0,
		paddingTop: token('space.200'),
	},
	accessButtonGroup: {
		display: 'flex',
		width: '100%',
		gap: token('space.050'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.medium'),
		overflow: 'hidden',
		padding: token('space.025'),
	},
	accessButtonSlot: {
		flex: '1 1 0',
	},
});

const SnippetBaseModalHeader: FC<{
	title: React.ReactNode;
	onClose: () => void;
}> = ({ title, onClose }) => {
	const { titleId } = useModal();
	return (
		<Inline
			alignInline="center"
			spread="space-between"
			alignBlock="center"
			xcss={styles.headerContainer}
		>
			<Heading size="small" id={titleId}>
				{title}
			</Heading>
			<CloseButton onClick={onClose} />
		</Inline>
	);
};

export const SnippetBaseModal: FC<SnippetBaseModalProps> = ({
	title,
	submitLabel,
	formKey,
	defaultValues,
	initialAdf,
	onSubmit,
	onClose,
	onCancel,
	onCloseComplete,
	contentId,
	spaceId,
	spaceKey,
	flags,
	isSnippetSharingForSiteEnabled,
}) => {
	const intl = useIntl();
	const [currentAdf, setCurrentAdf] = useState<DocNode>(initialAdf as DocNode);
	const [adfError, setADFError] = useState<unknown>(null);
	const { Modal } = useChoreographerAssets();

	const handleDocumentChange = (doc: DocNode) => {
		setCurrentAdf(doc);
	};

	const handleADFError = (error: unknown) => {
		setADFError(error);
	};

	const handleSubmit = async (values: SnippetBaseModalFormValues) => {
		await onSubmit(values, currentAdf);
	};

	const handleCancel = onCancel ?? onClose;

	return (
		<Modal width="95vw" shouldScrollInViewport onClose={onClose} onCloseComplete={onCloseComplete}>
			<Box xcss={styles.modalContent}>
				<SnippetBaseModalHeader title={title} onClose={onClose} />
				<Form<SnippetBaseModalFormValues> key={formKey} noValidate onSubmit={handleSubmit}>
					{({ submitting, formProps }) => (
						<form {...formProps} css={styles.formContainer}>
							<ModalBody>
								<Box xcss={styles.bodyWrapper}>
									<Box xcss={styles.bodyContainer}>
										<Box xcss={styles.previewColumn}>
											<SnippetEditor
												defaultValue={initialAdf}
												onDocumentChange={handleDocumentChange}
												onError={handleADFError}
												flags={flags}
												contentId={contentId}
												spaceId={spaceId}
												spaceKey={spaceKey}
											/>
										</Box>
										<Box xcss={styles.formColumn}>
											<Stack space="space.300" xcss={styles.formFields}>
												<Field
													name="snippetName"
													label={<FormattedMessage {...messages.nameLabel} />}
													defaultValue={defaultValues.snippetName}
													component={({ fieldProps }) => (
														<Textfield
															{...fieldProps}
															maxLength={MAX_SNIPPET_NAME_LENGTH}
															placeholder={intl.formatMessage(messages.namePlaceholder)}
														/>
													)}
													validate={(value) =>
														value ? undefined : intl.formatMessage(messages.nameError)
													}
													isRequired
												/>
												<CharacterCounterField<string, HTMLTextAreaElement>
													name="snippetDescription"
													label={<FormattedMessage {...messages.descriptionLabel} />}
													defaultValue={defaultValues.snippetDescription}
													maxCharacters={MAX_SNIPPET_DESCRIPTION_LENGTH}
												>
													{({ fieldProps }) => (
														<TextArea
															{...fieldProps}
															placeholder={intl.formatMessage(messages.descriptionPlaceholder)}
														/>
													)}
												</CharacterCounterField>
												{isSnippetSharingForSiteEnabled && (
													<Field<boolean>
														name="isShared"
														label={<FormattedMessage {...messages.accessLabel} />}
														defaultValue={defaultValues.isShared}
													>
														{({ fieldProps }) => (
															<Inline
																role="group"
																aria-labelledby={fieldProps['aria-labelledby']}
																space="space.050"
																xcss={styles.accessButtonGroup}
															>
																<Box xcss={styles.accessButtonSlot}>
																	<Tooltip
																		content={intl.formatMessage(messages.accessPrivateTooltip)}
																		position="bottom"
																	>
																		<Button
																			appearance="subtle"
																			isSelected={fieldProps.value === false}
																			onClick={() => fieldProps.onChange(false)}
																			aria-pressed={fieldProps.value === false}
																			shouldFitContainer
																		>
																			<FormattedMessage {...messages.accessPrivate} />
																		</Button>
																	</Tooltip>
																</Box>
																<Box xcss={styles.accessButtonSlot}>
																	<Tooltip
																		content={intl.formatMessage(messages.accessSharedTooltip)}
																		position="bottom"
																	>
																		<Button
																			appearance="subtle"
																			isSelected={fieldProps.value === true}
																			onClick={() => fieldProps.onChange(true)}
																			aria-pressed={fieldProps.value === true}
																			shouldFitContainer
																		>
																			<FormattedMessage {...messages.accessShared} />
																		</Button>
																	</Tooltip>
																</Box>
															</Inline>
														)}
													</Field>
												)}
												<Field
													name="snippetBody"
													defaultValue={adfError}
													validate={() =>
														adfError ? intl.formatMessage(messages.adfError) : undefined
													}
												>
													{({ error }) => (
														<MessageWrapper>
															{error && <ErrorMessage>{error}</ErrorMessage>}
														</MessageWrapper>
													)}
												</Field>
											</Stack>
											<Box xcss={styles.formActions}>
												<FormFooter align="end">
													<ButtonGroup>
														<Button appearance="subtle" onClick={handleCancel}>
															<FormattedMessage {...messages.cancelButton} />
														</Button>
														<Button type="submit" appearance="primary" isLoading={submitting}>
															{submitLabel}
														</Button>
													</ButtonGroup>
												</FormFooter>
											</Box>
										</Box>
									</Box>
								</Box>
							</ModalBody>
						</form>
					)}
				</Form>
			</Box>
		</Modal>
	);
};
